/**
 * THE CABINET'S SIDEBAR, AS DATA - which screens it lists, in what order, and which one is current.
 *
 * ═══ WHY IT EXISTS ═══
 * The first live browser run of the cabinet (2026-09-23) signed an author in through `/enter` and
 * landed them on `/dashboard` with NO WAY TO ANY OTHER SCREEN: not one link, not one button, and no
 * way to sign out. Every screen was mounted by `src/router.tsx` and each stood alone - the router's
 * own header said the nav "lives inside the screens' own shell components", and no screen had one.
 * The descriptors had carried a `label` documented as "What the nav calls it" since before the
 * router existed, and nothing read it.
 *
 * ═══ NOT A NEW LIST OF THE SAME SCREENS ═══
 * Every entry is read off a descriptor another file already wrote - `CABINET_ROUTES`
 * (`features/cabinet/routes.ts`) for the cabinet's own screens, `SUPPORT_ROUTES`
 * (`routes/support/routes.tsx`) for the support route that sits inside the shell - so a screen
 * added to either list reaches the sidebar with no edit here. The back office's sidebar follows the
 * same rule for the same reason (`web/admin/src/features/shell/model/console-navigation.ts`). The
 * two entries for `/apply` - a rowless reader's application, an author's profile - take their
 * address from `APPLY_ROUTE` and their words from here, because that descriptor carries no label.
 *
 * ═══ WHAT IS LEFT OUT, BY RULE RATHER THAN BY NAME ═══
 *   - A PATH WITH A PARAMETER. `/articles/$articleId/edit` is reached from a row, never from a tab
 *     (its descriptor says so), and a sidebar entry cannot name one article among many.
 *   - A SCREEN ITS DESCRIPTOR KEEPS OUT (`inSidebar: false`), which lights its parent instead: by
 *     the longest-prefix rule below (`/payments/method`, `/notifications/preferences`), or by
 *     `SIDEBAR_PARENTS` where its path names none (`/comments`, `/messages`: the Inbox's tabs).
 *   - A SUPPORT ROUTE OUTSIDE THE SHELL. `/terms` is an interstitial that must not show the cabinet
 *     nav (`insideShell: false`, with its reason, in `routes/support/routes.tsx`).
 * The ORDER is the descriptors' own - Overview, Articles, Statistics, Inbox, Payments, Calendar,
 * then the account's Notifications and Settings, which the prototype does not draw - then the
 * author's profile, then Support, last, where the prototype's own nav puts both
 * ("... Calendar & Ideas, Author Profile, Support", `features/cabinet/routes.ts`). Navigation
 * is ours and never the owner's: "layout, wording, navigation and screen flow are the app's own
 * business" (`docs/design/09-authors-app.md`).
 */
import { CABINET_ROUTES, SIDEBAR_PARENTS } from '../cabinet/routes';
import { APPLY_ROUTE } from '../../routes/apply';
import { SUPPORT_ROUTES } from '../../routes/support/routes';
import { isLiveApiConfigured } from '../../config';
import type { GateRequirement } from '../auth/session/gate';
import { cabinetIsReachable } from '../auth/session/session-state';
import type { SessionState } from '../auth/session/session-state';

/** One entry of the cabinet's sidebar, whichever descriptor declared it. */
export interface CabinetNavEntry {
  /** The descriptor's own words, never retyped here. */
  readonly label: string;
  /** Absolute and without a basename: the cabinet owns the whole of `authors.moonx.app`. */
  readonly path: string;
  /**
   * What the gate asks of a session before the screen renders (`features/auth/session/gate.ts`).
   * In a live build an entry the session cannot open is ABSENT, not disabled: a link that can only
   * bounce the reader somewhere else is a link they learn not to trust.
   */
  readonly requirement: GateRequirement;
}

/** A path with a `$parameter` names one thing among many, so it is never a sidebar destination. */
function isDestination(path: string): boolean {
  return !path.split('/').some((segment) => segment.startsWith('$'));
}

/**
 * The way to the author's own profile, for somebody the cabinet IS open to. Once an application is
 * approved, `/apply` is where the profile is edited - measured live 2026-09-23: "Save this section"
 * on each of five sections, "Choose a different photograph", "Add a file", "Go to your cabinet" -
 * and nothing inside the shell linked there, so an approved author reached their own profile only
 * by typing its address.
 *
 * NO LONGER A LINK OUT OF THE SHELL (design 14, finding F13). It was: `/apply` kept its own frame,
 * like the doors, so following this entry dropped the sidebar and an approved author got back only
 * through the page's "Go to your cabinet" button. Now the profile is drawn inside the shell for
 * exactly the readers this entry is listed for (`applyInsideShell` below), and `currentNavPath`
 * lights it there like any other entry. Its requirement is the route's own gate - a session and
 * nothing more; `navEntriesFor` below keeps it from a reader with no author row, whose entry for
 * the same address is `APPLICATION_NAV_ENTRY`. An applicant has a row (design 25): it is theirs.
 */
export const PROFILE_NAV_ENTRY: CabinetNavEntry = {
  label: 'Author profile',
  path: APPLY_ROUTE.path,
  requirement: 'signed-in',
};

/** Every entry a cabinet session can open, in the sidebar's order. */
export const CABINET_NAV_ENTRIES: readonly CabinetNavEntry[] = [
  ...CABINET_ROUTES.filter((route) => isDestination(route.path) && route.inSidebar !== false).map(
    (route): CabinetNavEntry => ({
      label: route.label,
      path: route.path,
      requirement: 'cabinet',
    }),
  ),
  PROFILE_NAV_ENTRY,
  ...SUPPORT_ROUTES.flatMap((route): CabinetNavEntry[] =>
    route.insideShell && route.navLabel !== null && isDestination(route.path)
      ? [{ label: route.navLabel, path: route.path, requirement: 'signed-in' }]
      : [],
  ),
];

/**
 * The way back for somebody the cabinet is not open to: a reader with NO author row (design 25 -
 * an applicant has one, and the whole menu). They reach one screen inside the shell - `/support`,
 * which asks only for a session - and without this their sidebar would offer nothing but the page
 * they are on. For them `/apply` sits outside the shell (the application flow, in its own frame),
 * so this entry is never listed for a cabinet session, which gets `PROFILE_NAV_ENTRY` instead.
 */
export const APPLICATION_NAV_ENTRY: CabinetNavEntry = {
  label: 'Your application',
  path: APPLY_ROUTE.path,
  requirement: 'signed-in',
};

/**
 * The entries a reader can use. `cabinetReachable` (any author row since design 25) is the
 * session's own answer in a live build, and `true` in the default build, where nothing is gated
 * and every screen renders its fixture (`features/auth/session/SessionGate.tsx`). One entry
 * per address: with no row, `/apply` is the application, never "Author profile".
 */
export function navEntriesFor(cabinetReachable: boolean): readonly CabinetNavEntry[] {
  if (cabinetReachable) {
    return CABINET_NAV_ENTRIES;
  }
  return [
    APPLICATION_NAV_ENTRY,
    ...CABINET_NAV_ENTRIES.filter(
      (entry) => entry.requirement !== 'cabinet' && entry.path !== APPLICATION_NAV_ENTRY.path,
    ),
  ];
}

/**
 * ★ WHETHER `/apply` IS DRAWN INSIDE THE SHELL, for a session whose `cabinetReachable` is given -
 * design 14, finding F13.
 *
 * THE FINDING. The cabinet walk of 2026-10-01 followed "Your profile" from the sidebar and landed
 * on a page with no sidebar: the profile is edited at `/apply`, `/apply` was mounted outside the
 * shell for everybody, and an approved author left the cabinet to change their own name.
 *
 * THE ANSWER IS THE SIDEBAR'S OWN. The page sits inside the shell exactly when the sidebar names it
 * "Author profile" - anybody with an author row: an applicant since design 25, an approved author,
 * a disabled one - and keeps its own frame when it names it "Your application". So the label a
 * reader followed and the frame they land in are decided by one function, `navEntriesFor`, and can
 * never drift apart.
 *
 * WHY A READER WITH NO AUTHOR ROW STAYS OUTSIDE, why `/apply` was outside in the first place:
 * the shell is the cabinet's chrome, and a reader the cabinet is not open to would see a sidebar of
 * one other page (`/support`) around a flow that has its own single next step - the shell is
 * mounted INSIDE the gate precisely so that "a reader the gate is still deciding about, or sending
 * elsewhere, never sees a sidebar of screens that would refuse them" (src/router.tsx, `gated`). An
 * applicant's cabinet is open, empty, before approval (design 25, D6), so they stay inside it.
 *
 * A LIVE-BUILD QUESTION ONLY - `applyInsideShellFor` below. The default build has no session to
 * ask, and `/apply` keeps its own frame there, as it always did.
 */
export function applyInsideShell(cabinetReachable: boolean): boolean {
  return navEntriesFor(cabinetReachable).includes(PROFILE_NAV_ENTRY);
}

/**
 * `applyInsideShell` for THIS build and THIS session - what `src/router.tsx`'s `ApplyFrame` asks.
 * False in the default build whoever is signed in: nothing there is gated, its sidebar is not the
 * session's (`CabinetShell`'s `CabinetNavigation`), its `/apply` fixture is an applicant's, and the
 * accessibility leg measures `/apply` there as a page outside the shell. The branch is decided per
 * build, like every live-only branch in the shell, so it never flips between renders.
 */
export function applyInsideShellFor(state: SessionState): boolean {
  return isLiveApiConfigured() && applyInsideShell(cabinetIsReachable(state));
}

function segmentsOf(path: string): readonly string[] {
  return path.split('/').filter((segment) => segment !== '');
}

/**
 * The entry the reader is on: the one whose path is the LONGEST whole-segment prefix of `pathname`.
 * So `/articles/statistics` is its own entry, `/articles/<id>/edit` lights "Articles" - the list
 * the editor is opened from - and `/articlesx` lights nothing. An address `SIDEBAR_PARENTS` names
 * (`features/cabinet/routes.ts`) counts as its parent's, when the parent is listed: `/messages`
 * lights "Inbox", whose tab it opens (design 24, T17). `null` when no entry covers the address.
 */
export function currentNavPath(
  entries: readonly CabinetNavEntry[],
  pathname: string,
): string | null {
  const here = segmentsOf(pathname);
  // Each listed entry by its own path, then each parent by the address it is lit on.
  const candidates: Array<readonly [path: string, lit: string]> = [
    ...entries.map((entry) => [entry.path, entry.path] as const),
    ...[...SIDEBAR_PARENTS]
      .filter(([, parent]) => entries.some((entry) => entry.path === parent))
      .map(([address, parent]) => [address, parent] as const),
  ];
  let best: { readonly path: string; readonly depth: number } | null = null;
  for (const [path, lit] of candidates) {
    const own = segmentsOf(path);
    const covers =
      own.length > 0 &&
      own.length <= here.length &&
      own.every((segment, index) => here[index] === segment);
    if (covers && (best === null || own.length > best.depth)) {
      best = { path: lit, depth: own.length };
    }
  }
  return best === null ? null : best.path;
}

/** The catalogue key (`shell` namespace) an entry is drawn with. */
export type NavTextKey =
  | 'nav.overview'
  | 'nav.articles'
  | 'nav.statistics'
  | 'nav.inbox'
  | 'nav.payments'
  | 'nav.calendar'
  | 'nav.settings'
  | 'nav.profile'
  | 'nav.application'
  | 'nav.support';

/** The descriptors' entries, by address. */
const NAV_TEXT_KEYS: ReadonlyMap<string, NavTextKey> = new Map([
  ['/dashboard', 'nav.overview'],
  ['/articles', 'nav.articles'],
  ['/articles/statistics', 'nav.statistics'],
  ['/inbox', 'nav.inbox'],
  ['/payments', 'nav.payments'],
  ['/calendar', 'nav.calendar'],
  ['/settings', 'nav.settings'],
  ['/support', 'nav.support'],
]);

/**
 * ★ THE WORDS AN ENTRY IS DRAWN WITH, IN THE CABINET'S LANGUAGE (design 29, W5). The descriptors keep
 * their English `label` - the order, the specs and the screens' own headers read it - and the
 * sidebar draws the catalogue's words for the language chosen. The two entries for `/apply` are told
 * apart by which one they are, since they share an address. `null` - an entry added without a key
 * here - keeps its English label (test/features/shell/language.spec.tsx fails on one).
 */
export function navTextKeyOf(entry: CabinetNavEntry): NavTextKey | null {
  if (entry === APPLICATION_NAV_ENTRY) {
    return 'nav.application';
  }
  if (entry === PROFILE_NAV_ENTRY) {
    return 'nav.profile';
  }
  return NAV_TEXT_KEYS.get(entry.path) ?? null;
}
